import { SOC, VERSION_CLIENT } from '../../constants/company';
import { NAV_BAT, NAV_MAG, NAV_OUVRIER } from '../../constants/navigation';
import { ACCESS_ROLE, NOTIF_N, navDuRole } from '../../lib/access';
import { I18N, LANG } from '../../lib/i18n';
import { useEcran } from '../../lib/ecran';
import { entreesMenuPlus } from '../../lib/menu-plus';
import { useHistoriqueNavigation } from '../../lib/historique-navigation';
import { termesOffre } from '../../lib/terminologie';
import { C } from '../../theme/palette';
import { BasculeMix } from './BasculeMix.jsx';

/** Bouton sans habillage du navigateur : le dessin vient tout entier du style. */
const nu = { margin: 0, fontFamily: 'inherit', lineHeight: 'normal' };

/**
 * Cadre commun des écrans : en-tête bleu marine, bannière éventuelle, bandeau
 * de titre blanc, contenu sur une colonne et barre de navigation en bas.
 *
 * <p>C'est le gabarit de la maquette V288, référence visuelle du client, à
 * toutes les tailles d'écran : sur le bureau, l'application garde la même
 * colonne et la même barre du bas, dans la fenêtre centrée que pose `App`.
 */
export function Shell({
  children,
  active,
  onNav,
  soc,
  back,
  onBack,
  estMixte,
  univers,
  setUnivers,
  pageTitle,
}) {
  // La flèche sert d'abord la page (fermer une fiche ouverte) ; à défaut,
  // elle ramène à l'écran précédent dès qu'on a navigué, comme la maquette.
  const historique = useHistoriqueNavigation();
  // Téléphone, tablette ou ordinateur : reconnu de lui-même (lib/ecran.js).
  const ecran = useEcran();
  const ordi = ecran === 'ordinateur';
  // Largeur du contenu : une colonne de téléphone, plus large sur tablette,
  // large sur ordinateur (où le texte est aussi agrandi, voir plus bas).
  const largeur = ecran === 'telephone' ? 720 : ecran === 'tablette' ? 980 : 1120;
  const avecRetour = !!back || !!historique?.peutRevenir;
  const surRetour = back ? onBack : historique?.revenir;
  const isMag = estMixte ? univers === 'magasin' : soc?.secteur === 'magasin';
  // Le Mix en deux sociétés : le bouton Bâtiment / Magasin ouvre l'autre
  // société. L'ouvrier, rattaché à une seule, ne bascule pas.
  const soeur = ACCESS_ROLE === 'ouvrier' ? null : soc?.abonnement?.societeLiee;
  const trLocal = (s) => {
    const _p = soc?.pays;
    const _lg = !_p || _p === 'CH' ? 'fr-CH' : soc?.lang || LANG;
    return (I18N[_lg] && I18N[_lg][s]) || (I18N[LANG] && I18N[LANG][s]) || s;
  };
  const baseNav = ACCESS_ROLE === 'ouvrier' ? NAV_OUVRIER : isMag ? NAV_MAG : NAV_BAT;
  const nav = navDuRole(baseNav, isMag);
  // Le nom de la piece (Reglages → Documents) l'emporte sur le dictionnaire :
  // « Devis » choisi par le patron ne doit pas ressortir « Offre » en Suisse.
  const termes = termesOffre(soc, { estMag: isMag });
  const motsPiece = new Set([termes.mot, termes.Mot, termes.pluriel, termes.Pluriel]);
  const traduitTitre = (t) => (motsPiece.has(t) ? t : trLocal(t));
  const titreAffiche = (() => {
    if (!pageTitle) return null;
    const m = String(pageTitle).match(/^(\S+)\s+([\s\S]*)$/);
    // Le titre entier au dictionnaire (« 🗄️ Archives & sauvegarde ») passe
    // avant le mot seul, qui n'y est pas toujours. Seulement derrière une
    // icône, et sans le nom de pièce choisi par le patron : « Nouveau devis »
    // ne doit pas devenir « Nouvelle offre ».
    const avecIcone = m && !/[A-Za-zÀ-ÿ]/.test(m[1]);
    const nommePiece = [...motsPiece].some((w) => w && String(pageTitle).includes(w));
    const entier = avecIcone && !nommePiece ? trLocal(String(pageTitle)) : String(pageTitle);
    if (entier !== String(pageTitle)) return entier;
    // Sans icône (« Nouvelle facture », « Choisir un modèle ») : le titre
    // reste d'un seul tenant, sinon son premier mot resterait en français.
    if (m && !avecIcone) return nommePiece ? String(pageTitle) : trLocal(String(pageTitle));
    // Icône et mot dans deux textes séparés : la traduction de l'écran
    // reconnaît alors « Offres » seul (pas « 📄 Offres »).
    return m ? (
      <>
        {m[1]} {traduitTitre(m[2])}
      </>
    ) : (
      traduitTitre(String(pageTitle))
    );
  })();
  const entete = (
    <div style={{ background: C.navy }}>
      <div
        style={{
          padding: '10px 16px',
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          maxWidth: ordi ? 'none' : largeur,
          margin: '0 auto',
          boxSizing: 'border-box',
        }}
      >
        <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
          {avecRetour && (
            <button
              type="button"
              className="st-retour"
              onClick={surRetour}
              aria-label="Retour"
              style={{
                ...nu,
                background: 'none',
                border: 'none',
                color: '#fff',
                fontSize: 18,
                cursor: 'pointer',
                padding: 0,
              }}
            >
              ←
            </button>
          )}
          <div
            aria-hidden="true"
            style={{
              width: 26,
              height: 26,
              borderRadius: 7,
              background: C.red,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              color: '#fff',
              fontWeight: 900,
              fontSize: 13,
            }}
          >
            S
          </div>
          <div>
            <div style={{ fontSize: 13, fontWeight: 800, color: '#fff', letterSpacing: -0.4 }}>
              {soc?.nom || SOC.nom}
            </div>
            <div
              style={{
                fontSize: 7,
                color: 'rgba(255,255,255,.4)',
                textTransform: 'uppercase',
                letterSpacing: 1.2,
              }}
            >
              Espace pro · {VERSION_CLIENT}
            </div>
          </div>
        </div>
        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
          <span style={{ position: 'relative', display: 'inline-block' }}>
            <button
              type="button"
              aria-label="Notifications"
              // L'employé a sa propre cloche : ses messages, rendez-vous, plans, documents.
              onClick={() => onNav && onNav(ACCESS_ROLE === 'ouvrier' ? 'signaux' : 'notifs')}
              style={{
                ...nu,
                background: 'rgba(255,255,255,.12)',
                border: 'none',
                borderRadius: 99,
                width: 30,
                height: 30,
                color: '#fff',
                fontSize: 14,
                cursor: 'pointer',
                padding: 0,
              }}
            >
              🔔
            </button>
            {NOTIF_N > 0 && (
              <span
                style={{
                  position: 'absolute',
                  top: -3,
                  right: -3,
                  minWidth: 16,
                  height: 16,
                  borderRadius: 99,
                  background: C.red,
                  color: '#fff',
                  fontSize: 9,
                  fontWeight: 900,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  padding: '0 3px',
                  border: '1.5px solid ' + C.navy,
                }}
              >
                {NOTIF_N > 9 ? '9+' : NOTIF_N}
              </span>
            )}
          </span>
          {soeur ? (
            <BasculeMix soc={soc} soeur={soeur} />
          ) : estMixte && setUnivers ? (
            <div
              role="group"
              aria-label="Univers"
              style={{
                display: 'flex',
                background: 'rgba(255,255,255,.12)',
                borderRadius: 99,
                padding: 3,
              }}
            >
              {[
                ['chantier', '🏗️', 'Bâtiment'],
                ['magasin', '🛒', 'Magasin'],
              ].map(([u, ic, lb]) => {
                const on = univers === u;
                return (
                  <button
                    key={u}
                    type="button"
                    aria-pressed={on}
                    onClick={() => setUnivers(u)}
                    style={{
                      ...nu,
                      border: 'none',
                      cursor: 'pointer',
                      borderRadius: 99,
                      padding: '5px 9px',
                      fontSize: 9,
                      fontWeight: 700,
                      background: on ? '#fff' : 'transparent',
                      color: on ? C.navy : 'rgba(255,255,255,.8)',
                    }}
                  >
                    {ic} {lb}
                  </button>
                );
              })}
            </div>
          ) : (
            <span
              style={{
                fontSize: 8,
                color: 'rgba(255,255,255,.85)',
                fontWeight: 700,
                background: 'rgba(255,255,255,.12)',
                borderRadius: 99,
                padding: '4px 10px',
              }}
            >
              {isMag ? '🛒 Magasin' : '🏗️ Bâtiment'}
            </span>
          )}
        </div>
      </div>
    </div>
  );
  const banniere = (
    <>
      {soc?.banniere && soc.banniere.active && soc.banniere.texte ? (
        <div
          style={{
            background: soc.banniere.type === 'maintenance' ? C.red : C.teal,
            color: '#fff',
            padding: '9px 16px',
            fontSize: 11,
            fontWeight: 700,
            textAlign: 'center',
            lineHeight: 1.4,
          }}
        >
          {(soc.banniere.type === 'maintenance' ? '⚠️ ' : '📢 ') + soc.banniere.texte}
        </div>
      ) : null}
    </>
  );
  const titre = (
    <>
      {titreAffiche && (
        <div
          style={{
            background: C.white,
            borderBottom: '1px solid ' + C.border,
            padding: '9px 16px',
            fontSize: 12,
            fontWeight: 800,
            color: C.ink,
          }}
        >
          {titreAffiche}
        </div>
      )}
    </>
  );
  const contenu = (
    <>
      <div
        style={{
          flex: 1,
          overflowY: 'auto',
          WebkitOverflowScrolling: 'touch',
          overscrollBehavior: 'contain',
          // Les marges de côté restent celles du téléphone : des barres
          // collantes (« Générer l'offre ») s'y alignent. Pas de marge en
          // bas ici : une barre collante s'arrête au-dessus de cette marge, et
          // la page défilait sous « Générer l'offre », entre elle et les
          // onglets. La marge du bas est celle du contenu, plus bas.
          padding: ordi ? '20px 13px 0' : '13px 13px 0',
        }}
      >
        {/* Rien ne déborde de côté : la page ne glisse plus sous le doigt.
            « clip » et non « hidden » : les barres collantes collent encore. */}
        <div
          className="st-contenu"
          style={{
            maxWidth: largeur,
            margin: '0 auto',
            overflowX: 'clip',
            paddingBottom: ordi ? 40 : 24,
          }}
        >
          {children}
        </div>
      </div>
    </>
  );
  const itemsNav = nav.map(({ id, icon, label }) => ({
    id,
    icon,
    nom: id === 'devis' ? termes.Pluriel : trLocal(label),
  }));
  // Sur ordinateur, le menu de côté a la place de montrer aussi les modules
  // de « Plus » (comptabilité, équipe, stock…), à un clic.
  const modulesCote =
    ordi && ACCESS_ROLE !== 'ouvrier'
      ? entreesMenuPlus({ mag: isMag })
          .modules.filter(([id]) => !nav.some((n) => n.id === id))
          .map(([id, icon, label]) => ({ id, icon, nom: trLocal(label) }))
      : [];
  if (ordi) {
    // Ordinateur : le menu sur le côté, toujours visible, et le contenu sur
    // toute la largeur ; le texte, dessiné pour le téléphone, est agrandi
    // pour se lire à la distance d'un écran de bureau.
    return (
      <div
        className="st-shell"
        style={{ height: '100%', display: 'flex', flexDirection: 'column', background: C.bg }}
      >
        <div style={{ zoom: 1.18 }}>{entete}</div>
        {banniere}
        <div style={{ flex: 1, display: 'flex', minHeight: 0 }}>
          <nav
            aria-label="Menu"
            style={{
              width: 230,
              flexShrink: 0,
              background: C.navy,
              borderTop: '1px solid rgba(255,255,255,.08)',
              padding: '14px 10px',
              display: 'flex',
              flexDirection: 'column',
              gap: 4,
              overflowY: 'auto',
            }}
          >
            {[...itemsNav, ...(modulesCote.length ? [{ id: '—' }] : []), ...modulesCote].map(
              ({ id, icon, nom }) => {
                if (id === '—')
                  return (
                    <div
                      key="separation"
                      style={{ height: 1, background: 'rgba(255,255,255,.1)', margin: '8px 6px' }}
                    />
                  );
                const a = id === active;
                return (
                  <button
                    key={id}
                    type="button"
                    aria-current={a ? 'page' : undefined}
                    onClick={() => onNav(id)}
                    style={{
                      ...nu,
                      display: 'flex',
                      alignItems: 'center',
                      gap: 12,
                      width: '100%',
                      padding: '12px 14px',
                      borderRadius: 10,
                      border: 'none',
                      cursor: 'pointer',
                      textAlign: 'left',
                      background: a ? 'rgba(255,255,255,.14)' : 'transparent',
                      color: a ? '#fff' : 'rgba(255,255,255,.72)',
                      fontSize: 15,
                      fontWeight: a ? 800 : 600,
                    }}
                  >
                    <span style={{ fontSize: 20, width: 26, textAlign: 'center' }}>{icon}</span>
                    {nom}
                  </button>
                );
              },
            )}
          </nav>
          <div
            style={{
              flex: 1,
              minWidth: 0,
              display: 'flex',
              flexDirection: 'column',
              zoom: 1.18,
            }}
          >
            {titre}
            {contenu}
          </div>
        </div>
      </div>
    );
  }
  return (
    <div
      className="st-shell"
      style={{ height: '100%', display: 'flex', flexDirection: 'column', background: C.bg }}
    >
      {entete}
      {banniere}
      {titre}
      {contenu}
      <nav aria-label="Navigation du bas" style={{ background: C.navy, flexShrink: 0 }}>
        <div
          style={{
            display: 'flex',
            // La barre d'accueil de l'iPhone (le trait en bas) ne cache pas les onglets.
            paddingBottom: 'max(4px, env(safe-area-inset-bottom, 0px))',
            maxWidth: largeur,
            margin: '0 auto',
          }}
        >
          {nav.map(({ id, icon, label }) => {
            const a = id === active;
            return (
              <button
                key={id}
                type="button"
                aria-current={a ? 'page' : undefined}
                onClick={() => onNav(id)}
                style={{
                  ...nu,
                  flex: 1,
                  display: 'flex',
                  flexDirection: 'column',
                  alignItems: 'center',
                  padding: '9px 0 6px',
                  background: 'none',
                  border: 'none',
                  cursor: 'pointer',
                  position: 'relative',
                }}
              >
                {a && (
                  <span
                    style={{
                      position: 'absolute',
                      top: 0,
                      left: '22%',
                      right: '22%',
                      height: 2,
                      background: '#fff',
                      borderRadius: 2,
                    }}
                  />
                )}
                <span
                  style={{
                    fontSize: ecran === 'tablette' ? 20 : 16,
                    color: a ? '#fff' : 'rgba(255,255,255,.3)',
                  }}
                >
                  {icon}
                </span>
                <span
                  style={{
                    fontSize: ecran === 'tablette' ? 11 : 7,
                    fontWeight: a ? 700 : 400,
                    color: a ? 'rgba(255,255,255,.85)' : 'rgba(255,255,255,.4)',
                  }}
                >
                  {id === 'devis' ? termes.Pluriel : trLocal(label)}
                </span>
              </button>
            );
          })}
        </div>
      </nav>
    </div>
  );
}
